import React, { useEffect, useRef, useState } from "react";
import { useStaticQuery, graphql } from "gatsby";
import { rhythm } from "../../utils/typography";
import styled from "@emotion/styled";
import { useForm } from "react-hook-form";

import { SendEmailProps } from "~/@types";
import { sendEmail } from "~/api/contact";

const FormWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  marginLeft: `auto`,
  marginRight: `auto`,
  maxWidth: rhythm(22),
  padding: 32,
  background: "var(--cardBg)",
  border: "1px solid var(--cardBorder)",
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.025)",
  "@media (max-width: 768px)": {
    padding: 24,
  },
  "& form": {
    width: "100%",
    marginBottom: 0,
  },
  "& button[type=submit]": {
    marginBottom: 0,
  },
});

const FormIntro = styled.div({
  marginBottom: rhythm(1.25),
  paddingBottom: rhythm(1.25),
  borderBottom: "1px solid var(--cardBorder)",
});

// Font values match the Material UI Typography this replaced
const bodyFont = {
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 400,
  letterSpacing: "0.00938em",
};

const Title = styled.h1({
  ...bodyFont,
  margin: "0 0 0.35em",
  fontSize: "1.25rem",
  lineHeight: 1.334,
  letterSpacing: 0,
  textAlign: "center",
  "@media (min-width: 600px)": {
    fontSize: "1.3118rem",
  },
  "@media (min-width: 960px)": {
    fontSize: "1.4993rem",
  },
});

const ThankYouTitle = styled.h2({
  ...bodyFont,
  margin: "0 0 0.35em",
  fontSize: "1.25rem",
  fontWeight: 500,
  lineHeight: 1.6,
  letterSpacing: "0.0075em",
  textAlign: "center",
});

const BodyText = styled.p({
  ...bodyFont,
  margin: 0,
  fontSize: "1rem",
  lineHeight: 1.5,
  textAlign: "center",
});

const InputWrapper = styled.div({
  marginBottom: `${rhythm(0.75)}`,
  "& label": {
    display: "block",
    marginBottom: `${rhythm(0.25)}`,
  },
  "& input, & textarea": {
    minWidth: "100%",
    borderRadius: 4,
    padding: 8,
    border: "1px solid var(--textLink)",
  },
});

const ErrorMessage = styled.p({
  marginTop: `${rhythm(0.25)}`,
  marginBottom: 0,
  fontSize: "0.9rem",
  color: "#c62828",
  "body.dark &": {
    color: "#ff8a80",
  },
});

const ThankYou = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "2.5rem",
});

type InputProps = {
  name: string;
  label: string;
  register: any;
  validation: Record<string, unknown>;
  error?: React.ReactNode;
  type?: string;
  autoComplete?: string;
  rows?: number;
};

const Input = ({
  label,
  name,
  register,
  validation,
  error,
  type = "text",
  autoComplete,
  rows,
}: InputProps) => {
  const inputId = `contact-${name}`;
  const errorId = `${inputId}-error`;
  const fieldProps = {
    id: inputId,
    name,
    ref: register(validation),
    required: true,
    "aria-invalid": Boolean(error),
    "aria-describedby": error ? errorId : undefined,
  };

  return (
    <InputWrapper>
      <label htmlFor={inputId}>{label}</label>
      {rows ? (
        <textarea {...fieldProps} rows={rows} />
      ) : (
        <input {...fieldProps} type={type} autoComplete={autoComplete} />
      )}
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </InputWrapper>
  );
};

const ContactForm = () => {
  const [isSent, setIsSent] = useState(false);
  const thankYouRef = useRef<HTMLHeadingElement>(null);
  const { handleSubmit, register, reset, errors } = useForm<SendEmailProps>();

  useEffect(() => {
    if (isSent) {
      thankYouRef.current?.focus();
    }
  }, [isSent]);

  const data = useStaticQuery(graphql`
    query ContactInfo {
      site {
        siteMetadata {
          contactEmail
        }
      }
    }
  `);

  const onSubmit = handleSubmit(async (data: SendEmailProps, e) => {
    try {
      if (!data?.password?.length) {
        await sendEmail(data);
      }

      setIsSent(true);
      reset();
    } catch ({ message }) {
      console.error(message);
    }
  });

  return (
    <FormWrapper>
      <FormIntro>
        <Title>Let's build something great together.</Title>
        <BodyText>
          Have a role, a project, or just want to talk shop?
          <br />
          Send a note here or email{" "}
          <a href={`mailto:${data.site.siteMetadata.contactEmail}`}>
            {data.site.siteMetadata.contactEmail}
          </a>
          .
        </BodyText>
      </FormIntro>

      {!isSent ? (
        <form onSubmit={onSubmit} noValidate>
          <input
            type="text"
            name="password"
            ref={register}
            style={{ display: "none" }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          ></input>
          <Input
            label="Name"
            name="name"
            autoComplete="name"
            register={register}
            validation={{ required: "Please enter your name." }}
            error={errors.name?.message}
          />
          <Input
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            register={register}
            validation={{
              required: "Please enter your email address.",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address.",
              },
            }}
            error={errors.email?.message}
          />
          <Input
            label="Message"
            name="body"
            rows={5}
            register={register}
            validation={{ required: "Please enter a message." }}
            error={errors.body?.message}
          />
          <button type="submit">Submit</button>
        </form>
      ) : (
        <ThankYou role="status">
          <ThankYouTitle ref={thankYouRef} tabIndex={-1}>
            Thank You!
          </ThankYouTitle>
          <BodyText>I usually respond within 24 hours.</BodyText>
        </ThankYou>
      )}
    </FormWrapper>
  );
};

export default ContactForm;
